import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText } from 'lucide-react';
import clsx from 'clsx';
import { processImportFile, ImportFileResult } from '../../utils/playbookImportUtils';
import { PlaybookEditState } from '../../pages/labs/PlaybookEditPage';

interface ImportOverlayProps {
  isVisible: boolean;
  isDragActive: boolean; // New prop to indicate global drag state
  onImportFiles: (results: ImportFileResult[]) => void;
  onClose: () => void;
  playbookState?: PlaybookEditState;
  className?: string;
}

const ImportOverlay: React.FC<ImportOverlayProps> = ({
  isVisible,
  isDragActive, // Use the new prop
  onImportFiles,
  onClose,
  playbookState,
  className
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Process files and validate them
  const processFiles = useCallback(async (files: File[]) => {
    setIsProcessing(true);
    
    try {
      const results: ImportFileResult[] = [];
      
      for (const file of files) {
        const result = await processImportFile(file, playbookState);
        results.push(result);
      }

      onImportFiles(results);
    } catch (error) {
    } finally {
      setIsProcessing(false);
    }
  }, [playbookState, onImportFiles]);

  // Handle drop events on the overlay
  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    
    if (files.length > 0) {
      await processFiles(files);
    }
  }, [processFiles]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Handle file input change
  const handleFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      await processFiles(files);
    }
    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [processFiles]);

  // Handle click to open file dialog
  const handleClick = useCallback(() => {
    if (!isProcessing) {
      fileInputRef.current?.click();
    }
  }, [isProcessing]);


  // Handle escape key to close overlay
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={clsx(
          "fixed inset-0 z-100 flex items-center justify-center",
          "bg-black bg-opacity-50 backdrop-blur-sm",
          className
        )}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className={clsx(
            "relative max-w-md w-full mx-4 p-8 rounded-lg shadow-xl",
            "bg-white dark:bg-gray-800 border-2 border-dashed",
            isDragActive 
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" 
              : "border-gray-300 dark:border-gray-600",
            "transition-colors duration-200"
          )}
          onClick={(e) => e.stopPropagation()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".yaml,.yml,.json"
            onChange={handleFileInputChange}
            className="hidden"
          />

          {/* Content */}
          <div className="text-center">
            {isProcessing ? (
              <div className="space-y-4">
                <div className="animate-spin mx-auto w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Processing Files...
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Please wait while we validate and process your files.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <motion.div
                  animate={isDragActive ? { scale: 1.1 } : { scale: 1 }}
                  className={clsx(
                    "mx-auto w-16 h-16 rounded-full flex items-center justify-center",
                    isDragActive 
                      ? "bg-blue-100 dark:bg-blue-900/30" 
                      : "bg-gray-100 dark:bg-gray-700"
                  )}
                >
                  {isDragActive ? (
                    <Upload className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  ) : (
                    <FileText className="w-8 h-8 text-gray-600 dark:text-gray-400" />
                  )}
                </motion.div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {isDragActive ? 'Drop files to import' : 'Import Playbook Files'}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {isDragActive 
                      ? 'Release to import your playbook and connection files'
                      : 'Drag and drop your playbook.yaml and connection.yaml files here, or click to browse'
                    }
                  </p>
                </div>

                {!isDragActive && (
                  <button
                    onClick={handleClick}
                    className={clsx(
                      "px-6 py-2 rounded-md font-medium transition-colors",
                      "bg-blue-600 hover:bg-blue-700 text-white",
                      "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                      "dark:focus:ring-offset-gray-800"
                    )}
                  >
                    Choose Files
                  </button>
                )}

                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  <p>Supported files: playbook.yaml, connection.yaml</p>
                  <p>Formats: YAML (.yaml, .yml) and JSON (.json)</p>
                </div>
              </div>
            )}
          </div>

          {/* Close button */}
          {!isProcessing && (
            <button
              onClick={onClose}
              className={clsx(
                "absolute top-4 right-4 p-1 rounded-full",
                "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300",
                "hover:bg-gray-100 dark:hover:bg-gray-700",
                "transition-colors duration-200"
              )}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ImportOverlay;
